import { isRecord } from './decode'

export type ProblemDetails = {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  code?: string
  line?: number
  column?: number
}

export const GENERIC_API_ERROR_MESSAGE = 'The server couldn’t complete this request. Try again.'

// An unhandled exception reaches the client as a 5xx with the generic ERROR code; every other detail is written for people.
function writtenDetail(status: number, problem: ProblemDetails | null): string | undefined {
  if (status >= 500 && (!problem?.code || problem.code === 'ERROR')) return undefined
  return problem?.detail
}

export class ApiError extends Error {
  readonly status: number
  readonly body: string
  readonly problem: ProblemDetails | null

  constructor(
    status: number,
    body: string,
    problem: ProblemDetails | null = null,
  ) {
    super(writtenDetail(status, problem) ?? GENERIC_API_ERROR_MESSAGE)
    this.status = status
    this.body = body
    this.problem = problem
  }

  get code(): string | undefined {
    return this.problem?.code
  }

  get serverMessage(): string | undefined {
    return writtenDetail(this.status, this.problem)
  }
}

export async function ignoreAlreadyGone(
  action: () => Promise<void>,
): Promise<void> {
  try {
    await action()
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 404)) {
      throw error
    }
  }
}

function stringProp(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function numberProp(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined
}

export function parseProblem(body: string): ProblemDetails | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return null
  }
  if (!isRecord(parsed)) return null
  return {
    type: stringProp(parsed.type),
    title: stringProp(parsed.title),
    status: numberProp(parsed.status),
    detail: stringProp(parsed.detail),
    instance: stringProp(parsed.instance),
    code: stringProp(parsed.code),
    line: numberProp(parsed.line),
    column: numberProp(parsed.column),
  }
}
